import {
  collectibleDropHtml,
  combatMessageLog,
  equipmentDropHtml,
  ITEM_ICON_TOKEN,
  itemDropHtml,
  recipeDropHtml,
} from '@helpers/combat/combat-log';
import { getEntry } from '@helpers/content/content';
import { isRecipeDiscovered, recipeDiscover } from '@helpers/crafting/recipes';
import { gatherVfxEmit } from '@helpers/engine/gather-vfx';
import { playGameSFX } from '@helpers/engine/sfx';
import { globalEffectSumsState } from '@helpers/state-game';
import {
  collectiblesAdd,
  isCollectibleDiscovered,
} from '@helpers/item/collectibles';
import { assertNeverReward } from '@helpers/item/loot';
import { addMaterialIncome, goldCoinId } from '@helpers/item/materials';
import { armoryAddLootDrop } from '@helpers/kingdom/loot-filter';
import {
  isWorkerRescued,
  workerRescue,
} from '@helpers/worker/worker-discovery';
import { rewardContentInfo } from '@helpers/world-node/world-node-rewards';
import type {
  AdventureLogRewardPart,
  CollectibleContent,
  Combat,
  EquipmentContent,
  ItemContent,
  ItemId,
  RecipeContent,
  ResolvedDrop,
  RewardContentInfo,
  WorkerContent,
} from '@interfaces';

function emitRewardVfx(
  combat: Combat,
  info: RewardContentInfo | undefined,
  quantity: number,
  quote?: string,
): void {
  if (!info) return;

  gatherVfxEmit({ nodeName: combat.locationName, quantity, quote, ...info });
}

function goldGainMultiplier(): number {
  return 1 + globalEffectSumsState().goldGainMultiplierBonus;
}

// Only Explore/ExploreRandom combat sets encounterId/encounterRandomId - raids (raidTownId) never do.
function isExploreCombat(combat: Combat): boolean {
  return !!combat.encounterId || !!combat.encounterRandomId;
}

function resolvedDropNameHtml(drop: ResolvedDrop): string | undefined {
  switch (drop.kind) {
    case 'Item': {
      const item = getEntry<ItemContent>(drop.itemId);
      return item ? itemDropHtml(item, drop.quantity) : undefined;
    }
    case 'Equipment': {
      const equipment = getEntry<EquipmentContent>(drop.equipmentId);
      return equipment ? equipmentDropHtml(equipment) : undefined;
    }
    case 'Collectible': {
      const collectible = getEntry<CollectibleContent>(drop.collectibleId);
      return collectible ? collectibleDropHtml(collectible) : undefined;
    }
    case 'Recipe': {
      const recipe = getEntry<RecipeContent>(drop.recipeId);
      return recipe ? recipeDropHtml(recipe) : undefined;
    }
    case 'Worker':
      return getEntry<WorkerContent>(drop.workerId)?.name;
    default:
      return assertNeverReward(drop);
  }
}

// Icon-token-prefixed text plus its matching icon, for listing drops granted outside combat.
export function resolvedDropLogPart(
  drop: ResolvedDrop,
): AdventureLogRewardPart | undefined {
  const html = resolvedDropNameHtml(drop);
  const info = rewardContentInfo(drop);
  return html && info
    ? { html: `${ITEM_ICON_TOKEN}${html}`, icon: info }
    : undefined;
}

// A worker reward always rolls its chance but only ever grants once - the already-rescued check is here.
export function grantResolvedDrops(
  combat: Combat,
  drops: ResolvedDrop[],
): void {
  const itemsFound: Record<ItemId, number> = {};
  let foundMajorReward = false;

  drops.forEach((drop) => {
    switch (drop.kind) {
      case 'Equipment': {
        const outcome = armoryAddLootDrop(drop.equipmentId, 'Combat');

        if (outcome.kind === 'NoRoom') {
          combatMessageLog(
            combat,
            'Your armory is full. An item drop was lost.',
          );
          return;
        }
        if (outcome.kind === 'UnknownContent') return;

        const info = rewardContentInfo(drop);

        if (outcome.kind === 'AutoSold') {
          const goldItem = getEntry<ItemContent>(goldCoinId());
          if (!goldItem) return;

          combatMessageLog(
            combat,
            `You automatically sold ${ITEM_ICON_TOKEN}${equipmentDropHtml(outcome.content)} for ${itemDropHtml(goldItem, outcome.goldEarned)}.`,
            undefined,
            info,
          );
          return;
        }

        combatMessageLog(
          combat,
          `The party found ${ITEM_ICON_TOKEN}${equipmentDropHtml(outcome.content)}!`,
          undefined,
          info,
        );
        emitRewardVfx(combat, info, 1);
        return;
      }

      case 'Collectible': {
        if (!isCollectibleDiscovered(drop.collectibleId)) {
          foundMajorReward = true;
        }
        collectiblesAdd(drop.collectibleId, 1);

        const collectible = getEntry<CollectibleContent>(drop.collectibleId);
        if (!collectible) return;

        const info = rewardContentInfo(drop);
        combatMessageLog(
          combat,
          `The party found ${ITEM_ICON_TOKEN}${collectibleDropHtml(collectible)}!`,
          undefined,
          info,
        );
        emitRewardVfx(combat, info, 1);
        return;
      }

      case 'Recipe': {
        if (isRecipeDiscovered(drop.recipeId)) return; // already found, no duplicate log entry

        recipeDiscover(drop.recipeId);
        foundMajorReward = true;

        const recipe = getEntry<RecipeContent>(drop.recipeId);
        if (!recipe) return;

        const info = rewardContentInfo(drop);
        combatMessageLog(
          combat,
          `The party found ${ITEM_ICON_TOKEN}${recipeDropHtml(recipe)}!`,
          undefined,
          info,
        );
        emitRewardVfx(combat, info, 1);
        return;
      }

      case 'Worker': {
        if (isWorkerRescued(drop.workerId)) return; // silent no-op, no duplicate

        workerRescue(drop.workerId);
        foundMajorReward = true;

        const worker = getEntry<WorkerContent>(drop.workerId);
        if (!worker) return;

        const info = rewardContentInfo(drop);
        combatMessageLog(
          combat,
          `The party rescued ${ITEM_ICON_TOKEN}${worker.name}!`,
          undefined,
          info,
        );
        if (worker.rescueText) {
          combatMessageLog(
            combat,
            `**${worker.name}**: *"${worker.rescueText}"*`,
          );
        }
        emitRewardVfx(combat, info, 1, worker.rescueText || undefined);
        return;
      }

      case 'Item': {
        itemsFound[drop.itemId] =
          (itemsFound[drop.itemId] ?? 0) + drop.quantity;
        return;
      }

      default:
        assertNeverReward(drop);
    }
  });

  Object.keys(itemsFound).forEach((itemId) => {
    let quantity = itemsFound[itemId as ItemId];
    if (quantity <= 0) return;

    if (itemId === goldCoinId() && isExploreCombat(combat)) {
      quantity = Math.round(quantity * goldGainMultiplier());
    }

    addMaterialIncome(itemId as ItemId, quantity);

    const item = getEntry<ItemContent>(itemId);
    if (!item) return;

    const info = rewardContentInfo({ itemId: itemId as ItemId });
    combatMessageLog(
      combat,
      `The party found ${ITEM_ICON_TOKEN}${itemDropHtml(item, quantity)}!`,
      undefined,
      info,
    );
    emitRewardVfx(combat, info, quantity);
  });

  if (foundMajorReward) playGameSFX('item-get-major');
}
